import React, { useEffect, useState } from 'react'
import type { MarketplaceBidCreateRequest, MarketplaceJob } from '../../../services/backendService'
import '../styles/BidSubmissionForm.css'

interface BidSubmissionFormProps {
  job: MarketplaceJob
  isSubmitting?: boolean
  errorMessage?: string | null
  onSubmit: (bidData: MarketplaceBidCreateRequest) => Promise<void> | void
  onCancel: () => void
}

export const BidSubmissionForm: React.FC<BidSubmissionFormProps> = ({
  job,
  isSubmitting = false,
  errorMessage,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    bidAmount: '',
    estimatedDays: '',
    validityHours: '48',
    notes: ''
  })

  useEffect(() => {
    if (!job.estimate) {
      return
    }

    setFormData(current => ({
      ...current,
      estimatedDays: current.estimatedDays || String(Math.max(1, Math.ceil((job.estimate?.printTimeMinutes || 0) / (60 * 8))))
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.estimate?.printTimeMinutes])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    await onSubmit({
      bidAmount: parseFloat(formData.bidAmount),
      estimatedDays: parseInt(formData.estimatedDays, 10),
      validityHours: parseInt(formData.validityHours, 10),
      notes: formData.notes,
      profileOverrides: job.printProfileSnapshot || undefined,
      estimatedMaterialGrams: job.estimate?.materialGrams || undefined,
      estimatedPrintTimeMinutes: job.estimate?.printTimeMinutes || undefined,
      estimatedMaterialCost: job.estimate?.materialCost || undefined,
      estimatedTotalCost: job.estimate?.totalCost || undefined
    })
  }

  return (
    <div className="bid-submission-form">
      <button className="btn-back" onClick={onCancel}>
        ← Back
      </button>

      <div className="form-container">
        <h2>Submit Bid for: {job.title}</h2>

        {job.pricingModel === 'fixed_price' && typeof job.fixedPrice === 'number' && (
          <div className="pricing-note">
            Doctor asking price: ${job.fixedPrice.toFixed(2)}
          </div>
        )}

        {job.estimate && (
          <div className="pricing-note">
            Estimated material: {job.estimate.materialGrams?.toFixed(2)} g · print time: {job.estimate.printTimeMinutes} min · estimated floor: ${job.estimate.totalCost?.toFixed(2)}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Bid Amount ($) *</label>
            <input
              type="number"
              name="bidAmount"
              value={formData.bidAmount}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              required
            />
          </div>

          <div className="form-group">
            <label>Estimated Manufacturing Days *</label>
            <input
              type="number"
              name="estimatedDays"
              value={formData.estimatedDays}
              onChange={handleChange}
              placeholder="Number of days"
              min="1"
              required
            />
          </div>

          <div className="form-group">
            <label>Bid Valid For *</label>
            <select name="validityHours" value={formData.validityHours} onChange={handleChange}>
              <option value="24">24 hours</option>
              <option value="48">48 hours</option>
              <option value="72">72 hours</option>
              <option value="168">7 days</option>
              <option value="336">14 days</option>
            </select>
            <div className="pricing-note">Your bid will expire if not accepted within this window.</div>
          </div>

          <div className="form-group">
            <label>Notes (Optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about your bid (e.g., special finishing, quality guarantees)..."
              rows={4}
            />
          </div>

          {errorMessage && <div className="form-error">{errorMessage}</div>}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting Bid...' : 'Submit Bid'}
            </button>
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

